import React, { useState } from "react";
import { Badge, Box, Button, Divider, Flex, Image, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { Globe, Home } from "react-feather";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { LanguagePill, LanguageSheet, useLanguagePreference } from "../LanguageSwitcher";
import LanguageFlag from "../LanguageFlag";
import { getPlatform } from "../../lib/platformUtils";
import { logoutToLanding } from "../../lib/logout";
import { partnerInitial, showsCreatorHomeDoor } from "../../lib/partnerNav";
import { tt } from "../../i18n/tt";

// 파트너 포털 상단 스트립 (2026-09-10 N3) — 로고 · 「파트너센터」(대표 지시 2026-09-11, 종전 「파트너」) · 언어(PC) · 프로필 단추.
// 메뉴는 여기 두지 않는다: PC 는 좌측 레일(PartnerRail), 휴대폰은 하단 탭(BottomNavBar).
// 크리에이터 TopNavBar 와 같은 규칙: iOS 는 status bar 만큼 위 여백, 언어는 PC 알약·휴대폰 바텀시트.

const TOP_INSET_STYLE = getPlatform() === "ios"
  ? { paddingTop: "max(env(safe-area-inset-top), 47px)" }
  : undefined;

export const PARTNER_STRIP_H = 48;

export default function PartnerTopStrip() {
  const { userInfo } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [langSheetOpen, setLangSheetOpen] = useState(false);
  const { lang: currentLang } = useLanguagePreference();
  const initial = partnerInitial(userInfo || {});
  // 크리에이터 겸 파트너(Creator, Partner)에게 크리에이터 홈으로 돌아가는 문 — 크리에이터 상단바 「파트너센터」 버튼과 짝
  // (2026-09-28 대표 결정 「파트너도 크리에이터처럼」, docs/ops/partner-works-browse-plan.md §4-2).
  // Admin 은 「어드민 확인용」 띠(PartnerAdminPreviewBand)에 같은 링크가 있어 중복하지 않는다. Partner 단독은 크리에이터 화면이 잠겨 있어 없다. 판정은 partnerNav.showsCreatorHomeDoor 한 곳.
  const showCreatorHome = showsCreatorHomeDoor(userInfo?.role);

  return (
    <Box as="header" position="sticky" top={0} zIndex={1000} bg="white" borderBottom="1px solid" borderColor="neutral.200" style={TOP_INSET_STYLE}>
      <Flex h={`${PARTNER_STRIP_H}px`} align="center" px={{ base: 4, md: 5 }} gap={2.5}>
        <Image src="/laeebly-icon.png" alt={t("topbar.logoAlt")} boxSize="26px" cursor="pointer" onClick={() => navigate("/partner")} />
        <Text fontWeight="700" fontSize="md" color="neutral.800" letterSpacing="-0.5px" cursor="pointer" onClick={() => navigate("/partner")}>
          {t("topbar.brand")}
        </Text>
        <Text fontSize="sm" color="neutral.500" pl={2.5} borderLeft="1px solid" borderColor="neutral.200" lineHeight="1">
          {tt("a.b4f21c8e", "파트너센터")}
        </Text>
        {/* 베타 표시 — 파트너 허브는 베타 버전 (대표 지시 2026-09-10). 홈의 안내 카드와 짝 */}
        <Badge colorScheme="orange" variant="subtle" fontSize="10px" borderRadius="full" px={2} lineHeight="1.6">{tt("a.a412f55f", "베타")}</Badge>

        <Flex ml="auto" align="center" gap={2}>
          {showCreatorHome && (
            <Button
              size="sm" h="30px" px={3} borderRadius="8px" fontSize="12.5px" fontWeight="700"
              variant="outline" colorScheme="brand"
              leftIcon={<Home size={14} />}
              display={{ base: "none", md: "inline-flex" }}
              title={tt("a.partnerCreator.toHomeTitle", "크리에이터 홈으로 — 작품 목록·채널·작품 신청·정산은 일반 회원과 같아요")}
              data-track="파트너 상단 크리에이터 홈"
              onClick={() => navigate("/home")}
            >
              {t("nav.creatorHome")}
            </Button>
          )}
          <LanguagePill />
          <Menu placement="bottom-end" autoSelect={false}>
            <MenuButton
              aria-label={tt("a.d6304d39", "프로필 메뉴")}
              data-guide="partner.profile"
              w="30px" h="30px" borderRadius="full"
              bg="brand.50" color="brand.700" fontSize="xs" fontWeight="700"
              _hover={{ bg: "brand.100" }} _active={{ bg: "brand.100" }}
            >
              {initial}
            </MenuButton>
            <MenuList minW="240px" borderRadius="8px" border="1px solid" borderColor="neutral.200" boxShadow="0 4px 16px rgba(0,0,0,0.08)" p={1} bg="white">
              <MenuItem py={2} px={3} mb={1} borderRadius="4px" _hover={{ bg: "neutral.50" }} onClick={() => navigate("/edit-profile")}>
                <Flex align="center" justify="space-between" w="100%">
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="600" color="neutral.800" noOfLines={1}>{userInfo?.name || tt("a.f06f9fdb", "파트너")}</Text>
                    <Text fontSize="xs" color="neutral.400" noOfLines={1}>{userInfo?.email}</Text>
                    <Text fontSize="xs" color="brand.600" fontWeight="600" mt={0.5}>{t("topbar.myInfo")} ›</Text>
                  </Box>
                  <ChevronRightIcon color="neutral.300" boxSize={4} />
                </Flex>
              </MenuItem>
              <Divider borderColor="neutral.100" />
              {/* 언어 — 휴대폰만(PC 는 위 알약). 누르면 메뉴가 닫히고 바텀시트가 열린다 */}
              <MenuItem display={{ base: "flex", md: "none" }} fontSize="sm" fontWeight="500" py={2} px={3} mt={1} borderRadius="4px" color="neutral.600" _hover={{ bg: "neutral.50" }} onClick={() => setLangSheetOpen(true)}>
                <Flex align="center" gap={2} w="100%">
                  <Box as={Globe} size={16} color="neutral.500" aria-hidden />
                  <Text as="span">{t("lang.label")}</Text>
                  <Flex align="center" gap={1.5} ml="auto">
                    <LanguageFlag lang={currentLang} size={18} />
                    <Text as="span" fontSize="xs" color="neutral.400">{t(`lang.${currentLang}`)}</Text>
                    <ChevronRightIcon color="neutral.300" boxSize={4} />
                  </Flex>
                </Flex>
              </MenuItem>
              {/* 계정 하위 항목(내 정보·비밀번호 변경)은 두지 않는다 — 위 프로필 블록 「내 정보 ›」와 중복 (대표 지시 2026-09-10). 비밀번호는 내 정보 안 「보안」 */}
              <Box py={1}>
                {/* 크리에이터 홈 — 겸직만. PC 상단 버튼의 휴대폰 짝 */}
                {showCreatorHome && (
                  <Box display={{ base: "block", md: "none" }}>
                    <MenuItem fontSize="sm" fontWeight="600" py={2} px={3} borderRadius="4px" color="brand.700" _hover={{ bg: "brand.50" }} onClick={() => navigate("/home")}>
                      <Flex align="center" gap={2}>
                        <Box as={Home} size={16} aria-hidden />
                        <Text as="span">{tt("a.partnerCreator.toHome", "크리에이터 홈 →")}</Text>
                      </Flex>
                    </MenuItem>
                  </Box>
                )}
                <MenuItem fontSize="sm" fontWeight="500" py={2} px={3} borderRadius="4px" color="status.error" _hover={{ bg: "red.50" }} onClick={logoutToLanding}>
                  {t("menu.logout")}
                </MenuItem>
              </Box>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>
      <LanguageSheet isOpen={langSheetOpen} onClose={() => setLangSheetOpen(false)} />
    </Box>
  );
}
